import { Dialog, DialogContent, DialogTitle } from '@flatfile/baseline'
import React, { Suspense, useCallback, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AppHTTPService } from '../../../api/http-service'
import { CustomErrorComponent, ErrorBoundaryErrorPage } from '../../../components/Shared/CustomErrorComponent'
import { useEntitlements } from '../../../hooks/useEntitlements'
import { useActiveEnvironment } from '../../../hooks/useEnvironments'
import { useEventSubscriber } from '../../../hooks/useEventSubscriber'
import { useCollection } from '../../../resources/hooks/useCollection'
import { useCreateResource } from '../../../resources/hooks/useResourceMutation'
import getCollection from '../../../resources/support/getCollection'
import { mixpanel } from '../../../support/mixpanel'
import { logger } from '../../../utils/logging'
import { AutobuildLoadingScreen, LoadingStage } from './AutobuildLoadingScreen'
import { ACCEPTED_XLSX_FILE_EXTENSIONS, ConfigureAgentForm } from './ConfigureAgentForm'
import { Header } from './Header'
import { SpaceStep } from './SpaceStep'

// Inline the getJob function that was previously in Shared.tsx
const getJob = async (id: string) => {
  return AppHTTPService.client.jobs.get(id)
}

// Check for running ai-generate-blueprint jobs for a specific app
const getRunningAutobuildJob = async (appId: string, environmentId: string) => {
  try {
    const { data: jobs } = await AppHTTPService.client.jobs.list({
      environmentId,
      source: appId,
    })

    // Filter for ai-generate-blueprint jobs that are still running for this app
    const runningJob = jobs.find(
      (job) =>
        job.source === appId &&
        job.operation === 'ai-generate-blueprint' &&
        job.status &&
        ['created', 'planning', 'ready', 'executing'].includes(job.status as string)
    )

    return runningJob
  } catch (error) {
    logger.error('Error checking for running autobuild jobs:', error)
    return null
  }
}

const ConfigureStep: React.FC<{ onSubmit: (_data: any) => void; error?: string }> = ({ onSubmit, error }) => {
  return (
    <div className='flex flex-col h-full'>
      <Header />
      <div className='flex-1 flex flex-col items-center justify-start w-full overflow-y-auto'>
        <div className='flex-none w-full'>
          <ConfigureAgentForm onSubmit={onSubmit} error={error} />
        </div>
      </div>
    </div>
  )
}

export const AutoBuildContent: React.FC<{}> = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [blueprintJobId, setBlueprintJobId] = useState<string | null>(null)
  const [appIdInvalid, setAppIdInvalid] = useState(false)
  const [loadingStage, setLoadingStage] = useState<LoadingStage>('initializing')
  const [jobStatus, setJobStatus] = useState<string>('')
  const [jobProgress, setJobProgress] = useState<number | undefined>(undefined)

  const apps = useCollection('APPS')
  const navigate = useNavigate()
  const { id: appId, step } = useParams<{ id: string; step: string }>()
  const [createAutoBuild] = useCreateResource('AUTOBUILD')
  const { environment } = useActiveEnvironment()
  const [createAgent] = useCreateResource('AGENTS')
  const [searchParams] = useSearchParams()
  const { hasEntitlement } = useEntitlements()

  const hasAutobuildV2 = hasEntitlement('autobuildV2')
  const completedOperation = hasAutobuildV2 ? 'reconfigure' : 'ai-generate-blueprint'

  // Detect if we're in onboarding mode (coming from FirstLogin with welcome=true)
  const isOnboardingMode = searchParams.get('welcome') === 'true'

  useEffect(() => {
    if (appId) {
      const existingApp = apps.find((a) => a.id === appId)
      if (existingApp) {
        return
      }
    }
    setAppIdInvalid(true)
  }, [apps, appId])

  // Check for completed autobuild and running jobs when component mounts
  useEffect(() => {
    const checkAutobuildStatus = async () => {
      if (appId && environment?.id && !isLoading && !error) {
        // First check if autobuild is already completed
        const currentApp = apps.find((a) => a.id === appId)
        if (currentApp?.metadata?.autobuild && currentApp?.metadata?.templateCreated) {
          // Autobuild is complete, redirect to template page
          navigate(`/apps/custom/${appId}/build/template`, { replace: true })
          return
        }

        // If not completed, check for running jobs
        const runningJob = await getRunningAutobuildJob(appId, environment.id)
        if (runningJob) {
          setBlueprintJobId(runningJob.id)
          setIsLoading(true)
          setLoadingStage('generating')
        }
      }
    }

    checkAutobuildStatus()
  }, [appId, environment?.id, isLoading, error, apps, navigate])

  const handleConfigureWorkbook = useCallback(
    async ({ files, prompt, empty }: { files?: File[]; prompt?: string; empty?: boolean }) => {
      if (!files && !prompt && !empty) {
        setError('No input provided')
        return
      }
      try {
        // Track autobuild process start
        mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Process Started' : 'Autobuild Process Started', {
          hasFiles: !!files?.length,
          hasPrompt: !!prompt,
          isEmpty: !!empty,
          fileCount: files?.length || 0,
          promptLength: prompt?.length || 0,
          appId,
        })

        setIsLoading(true)
        setError(null)
        setBlueprintJobId(null)
        setJobStatus('') // Reset job status when starting new autobuild
        setJobProgress(undefined) // Reset job progress when starting new autobuild
        setLoadingStage('initializing')
        const formData = new FormData()
        if (files) {
          for (const file of files) {
            formData.append('file', file)
          }

          const hasXlsxFile = files.some((item) => {
            const ext = item.name.slice(item.name.lastIndexOf('.')).toLowerCase()
            return ACCEPTED_XLSX_FILE_EXTENSIONS.includes(ext)
          })

          if (hasXlsxFile) {
            const response = await fetch(
              'https://raw.githubusercontent.com/FlatFilers/agents/main/xlsx-extractor/dist/index.js'
            )
            const file = await response.text()

            createAgent({
              environmentId: environment.id,
              agentConfig: {
                topics: ['file:created', 'job:ready'],
                compiler: 'js',
                source: file,
                slug: 'xlsx-extractor',
              },
            })
          }
        }
        if (prompt) {
          formData.append('prompt', prompt)
        }

        await createAutoBuild({
          appId: appId!,
          environmentId: environment.id,
          empty,
          formData,
        })

        // For empty autobuilds, no job is created, so proceed directly
        if (empty) {
          handleSuccess()
          return
        }
      } catch (e) {
        logger.error('Error in autobuild process:', e)
        setIsLoading(false)
        setError('An error occurred while creating your Autobuild instance. Please try again.')

        // Track autobuild process failure
        mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Process Failed' : 'Autobuild Process Failed', {
          isOnboarding: isOnboardingMode,
          errorMessage: 'An error occurred while creating your Autobuild instance. Please try again.',
          error: e instanceof Error ? e.message : 'Unknown error',
          hasFiles: !!files?.length,
          hasPrompt: !!prompt,
          isEmpty: !!empty,
          appId,
        })
      }
    },
    [createAutoBuild, environment.id, apps, createAgent, appId]
  )

  const handleEventForCurrentApp = useCallback(
    (event: any, operations: string[], action: () => void) => {
      // First check if this is for the current app
      if (event?.context?.appId !== appId) {
        return
      }

      // Then check if this is one of our expected operations
      if (!operations.includes(event?.payload?.operation)) {
        return
      }

      // Only if both checks pass, execute the action
      action()
    },
    [appId]
  )

  const handleSuccess = useCallback(async () => {
    // Track autobuild success
    mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Process Completed' : 'Autobuild Process Completed', {
      isOnboarding: isOnboardingMode,
      appId,
    })

    setBlueprintJobId(null)
    setIsLoading(false) // Reset loading state so SpaceStep can render
    setError(null) // Clear any errors
    setJobStatus('') // Reset job status
    setJobProgress(undefined) // Reset job progress

    // Always route through SpaceStep to ensure consistent metadata setup
    navigate(`/apps/custom/${appId}/build/template`, { replace: true })
  }, [navigate, appId, isOnboardingMode])

  const handleError = useCallback(
    async ({ event, errorMessage }: { event?: any; errorMessage?: string }) => {
      if (event) {
        const job = await getJob(event.context.jobId)
        const outcomeMessage = job.data?.outcome?.message
        const reasonMatch = outcomeMessage.match(/Reason: (.*)/)
        const reason = reasonMatch?.[1] || 'Unable to generate your workbook. Please try again.'

        // Track job-specific errors
        mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Job Failed' : 'Autobuild Job Failed', {
          isOnboarding: isOnboardingMode,
          jobId: event.context.jobId,
          errorMessage: reason,
          outcomeMessage,
          appId,
        })

        setBlueprintJobId(null)
        setError(reason)
        setIsLoading(false)
        setJobStatus('')
        setJobProgress(undefined)
      }
      if (errorMessage) {
        // Track generic errors
        mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Process Error' : 'Autobuild Process Error', {
          isOnboarding: isOnboardingMode,
          errorMessage,
          appId,
        })

        setBlueprintJobId(null)
        setError(errorMessage)
        setIsLoading(false)
        setJobStatus('')
        setJobProgress(undefined)
      }
    },
    [setError, setIsLoading]
  )

  const handleJobEvent = useCallback(
    (event: any, operations: string[], requireComplete: boolean = false) => {
      handleEventForCurrentApp(event, operations, async () => {
        // For job:updated events, we need to check the status
        if (requireComplete && event.payload.status !== 'complete') {
          return
        }

        await handleSuccess()
      })
    },
    [handleEventForCurrentApp, handleSuccess]
  )

  // Poll for job status updates
  useEffect(() => {
    if (!blueprintJobId) return

    setLoadingStage('generating')

    const pollJobStatus = async () => {
      try {
        const { data: job } = await getJob(blueprintJobId)
        if (job.info) {
          setJobStatus(job.info)
        }
        if (job.progress !== undefined) {
          setJobProgress(job.progress)
        }
      } catch (err) {
        logger.error('Error polling job status:', err)
      }
    }

    // Poll immediately and then every 2 seconds
    pollJobStatus()
    const intervalId = setInterval(pollJobStatus, 2000)

    return () => {
      clearInterval(intervalId)
    }
  }, [blueprintJobId, handleSuccess, handleError])

  useEventSubscriber(['job:completed'], (event) => {
    handleJobEvent(event, [completedOperation], true)
  })

  useEventSubscriber(['job:completed'], (event) => {
    handleEventForCurrentApp(event, [completedOperation], async () => {
      if (event?.context?.jobId) {
        // Track when blueprint job starts
        mixpanel.track(
          isOnboardingMode ? 'Buildmode Onboarding Blueprint Job Started' : 'Autobuild Blueprint Job Started',
          {
            isOnboarding: isOnboardingMode,
            jobId: blueprintJobId,
            appId,
          }
        )
      }
    })
  })

  useEventSubscriber(['job:created'], (event) => {
    handleEventForCurrentApp(event, ['ai-generate-blueprint'], async () => {
      if (event.context.jobId === blueprintJobId || blueprintJobId === null) {
        setBlueprintJobId(event?.context?.jobId)
      }
    })
  })

  useEventSubscriber(['job:failed'], async (event) => {
    handleEventForCurrentApp(event, ['ai-generate-blueprint', 'reconfigure'], async () => {
      if (event.context.jobId === blueprintJobId || blueprintJobId === null) {
        handleError({ event })
      }
    })
  })

  if (appIdInvalid) {
    return (
      <CustomErrorComponent
        page='autobuildflow-invalidappId'
        message='Unable to find App. Please check the URL or try again.'
      />
    )
  }

  if (isLoading) {
    // Track loading screen views
    mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Loading Screen Shown' : 'Autobuild Loading Screen Shown', {
      isOnboarding: isOnboardingMode,
      loadingStage,
      appId,
    })
    return (
      <AutobuildLoadingScreen
        stage={loadingStage}
        jobStatus={jobStatus}
        progress={jobProgress}
        hasAutobuildV2={hasAutobuildV2}
      />
    )
  }

  if (error) {
    // Track when users see the error state
    mixpanel.track(isOnboardingMode ? 'Buildmode Onboarding Error State Shown' : 'Autobuild Error State Shown', {
      isOnboarding: isOnboardingMode,
      errorMessage: error,
      appId,
    })
    return <ConfigureStep onSubmit={handleConfigureWorkbook} error={error} />
  }

  switch (step) {
    case 'configure':
      return <ConfigureStep onSubmit={handleConfigureWorkbook} />
    case 'template':
      return <SpaceStep appId={appId!} />
    case 'settings':
      return <SpaceStep appId={appId!} />
    default:
      return <Navigate to='/build/configure' replace />
  }
}

export const AutoBuildFlow: React.FC = () => {
  const navigate = useNavigate()

  const handleClose = async () => {
    // Refresh the apps collection to show the newly created app
    await getCollection('APPS').refresh()
    navigate('/home')
  }

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className='p-0 max-w-full h-full border-none z-[200] flex flex-col gap-0' fullscreen>
        <DialogTitle className='sr-only'>Autobuild Configuration</DialogTitle>
        <div className='bg-surface-base-elevated flex-1 flex flex-col overflow-hidden'>
          <ErrorBoundary
            fallbackRender={({ error }) => (
              <ErrorBoundaryErrorPage
                error={error}
                page='Autobuildflow.tsx'
                message='There was an error loading Autobuild.'
              />
            )}
          >
            <Suspense fallback={<AutobuildLoadingScreen stage='initializing' hasAutobuildV2={false} />}>
              <AutoBuildContent />
            </Suspense>
          </ErrorBoundary>
        </div>
      </DialogContent>
    </Dialog>
  )
}
